import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Typography, Button } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import {
	IPatientBase,
	IPatientTableProps,
} from 'src/features/patient/domain/types';
import {
	PATIENT_LOADING_KEY,
	getPatients,
} from 'src/features/patient/store/patientThunks';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import { CreateEditPatientDrawer } from './CreateEditPatientDrawer';

export const PatientTable: React.FC<IPatientTableProps> = ({ siteSlug }) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const [queryState, setQueryState] = useState<ITableQueryState>({
		page: 0,
	});
	const [patients, setPatients] = useState<IPatientBase[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

	useEffect(() => {
		const params = buildQueryParams(queryState);

		dispatch(
			getPatients({
				request: params,
				siteSlug: siteSlug,
				onSuccess: (response) => {
					setPatients(response.results);
					setTotalCount(response.info.count);
				},
			}),
		);
	}, [queryState, siteSlug]);

	const columns: IColumn<IPatientBase>[] = [
		{
			key: 'site_patient_id',
			label: 'Patient ID',
			sortable: false,
			searchable: true,
			render: (patient) => patient.site_patient_id,
		},
		{
			key: 'name',
			label: 'Patient Name',
			sortable: true,
			render: (patient) => `${patient.first_name} ${patient.last_name}`,
		},
		{
			key: 'gender',
			label: 'Gender',
			sortable: false,
			render: (patient) => patient.gender,
		},
		{
			key: 'age',
			label: 'Age',
			sortable: false,
			render: (patient) => patient.age,
		},
		{
			key: 'created_by',
			label: 'Created By',
			sortable: false,
			render: (patient) =>
				`${patient.created_by.first_name} ${patient.created_by.last_name}`,
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			render: (patient) => formatTimestamp(patient.created_at),
		},
		{
			key: 'action',
			label: 'Action',
			align: 'center',
			render: (patient) => (
				<Button
					variant='outlined'
					size='small'
					onClick={() =>
						navigate(`/site/${siteSlug}/patient/${patient.id}`)
					}
					sx={{
						textTransform: 'none',
						borderColor: '#BE3327',
						color: '#BE3327',
						display: 'flex',
						justifyContent: 'center',
						borderRadius: '20px',
						'&:hover': {
							borderColor: '#BE3327',
							backgroundColor: '#BE332730',
						},
					}}
				>
					View
				</Button>
			),
		},
	];

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				padding: 3,
				gap: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
				maxWidth: { xs: 'calc(100vw - 2.5rem)', sm: '100%' },
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					justifyContent: { xs: 'flex-start', sm: 'space-between' },
					alignItems: { xs: 'flex-start', sm: 'center' },
					gap: { xs: 2, sm: 0 },
					width: '100%',
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Patients
				</Typography>
				<Box
					sx={{
						width: { xs: '100%', sm: '231px' },
						height: '36px',
					}}
					className='hidden lg:block'
				>
					{siteDetails?.can_add_patients && (
						<PrimaryButton
							onClick={() => setIsCreateDrawerOpen(true)}
							sx={{
								height: '36px',
								minHeight: '36px',
								'& .MuiButton-root': {
									height: '36px',
									minHeight: '36px',
								},
								backgroundColor: '#BE3327',
								'&:hover': {
									backgroundColor: '#A52D23',
								},
							}}
						>
							Create New Patient
						</PrimaryButton>
					)}
				</Box>
			</Box>

			<DataTable
				columns={columns}
				data={patients}
				loadingKey={PATIENT_LOADING_KEY}
				emptyMessage='No patients found'
				paginationInfo={{ totalCount: totalCount }}
				queryState={queryState}
				onQueryChange={setQueryState}
			/>

			<CreateEditPatientDrawer
				open={isCreateDrawerOpen}
				onClose={() => setIsCreateDrawerOpen(false)}
				onSubmit={() => {
					setQueryState({
						page: 0,
					});
					setIsCreateDrawerOpen(false);
				}}
			/>
		</Box>
	);
};
